<template>
	<div :class="tooltipClasses">
		<div class="map-tooltip-header">
			{{ $t('home-map.tooltip.headers.' + featureType) }}
		</div>
		<div class="map-tooltip-content">
			<MountpointDisplay :baseStation="featureData" />
			<Badge v-if="showBaseStatus" :status="featureData.statusData.badge" :text="$t('baseStationStatus.' + featureData['status'])" />
			<Badge v-if="showLiveStatus" :status="featureData.statusLiveData.badge" :text="$t('baseStationStatus.' + featureData['status_live'])" />
			<Badge v-if="showOnlineStatus" :status="featureData.statusOnlineData.badge" :text="$t('baseStationStatus.' + featureData['status_online'])" />

			<div v-if="failingGnssCodes.length > 0" class="pd-vertical">
				<div class="subtitle">{{ $t('home-map.tooltip.gssOverlay') }}</div>
				<div>
					<GnssTag
						v-for="code in failingGnssCodes"
						:key="code"
						:code="code"
					/>
				</div>
			</div>

			<div v-if="!featureData['reliable_coords']" class="pd-vertical">
				<Tag color="pink">
					{{ $t('base-stations-edit.fields.coordsCorrectionsHelp.nonReliableCoords1') }}
				</Tag>
				<div class="pd non-reliable-coords-text">
					{{ $t('base-stations-edit.fields.coordsCorrectionsHelp.nonReliableCoords2') }}
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { computed, toRef } from 'vue';
import type { PropType } from 'vue';

import { Badge, Tag } from 'ant-design-vue';

import { useLocaleData } from '@/locales/useLocaleData';
import { useUserStore } from '@/stores/user';

import MountpointDisplay from '@/components/baseStations/MountpointDisplay';
import GnssTag from '@/components/common/GnssTag.vue';
import type { FeatureType, BaseStationStyleFeatureData } from '@/components/map/types/mapFeatureTypes';

export default {
	components: {
		Badge, Tag,
		MountpointDisplay,
		GnssTag,
	},
	props: {
		hoveredFeatureData: {
			type: Object as PropType<BaseStationStyleFeatureData>,
			required: true,
		},
		featureType: {
			type: String as PropType<FeatureType>,
			required: true,
		},
	},
	setup(props) {
		const featureData = toRef(props, 'hoveredFeatureData');
		const userStore = useUserStore();
		const showBaseStatus = computed(() => featureData.value.status !== 'working');
		const showOnlineStatus = computed(() => !showBaseStatus.value);
		const showLiveStatus = computed(() => showOnlineStatus.value && userStore.allowToSeeLiveStatus);
		const failingGnssCodes = computed(() => (
			featureData.value.status === 'working'
				? (featureData.value.gssFailingGnss ?? [])
				: []
		));
		const { localeData } = useLocaleData();
		const tooltipClasses = computed(() => [
			'map-tooltip-style',
			'map-tooltip-position-' + localeData.value.direction,
			'no-pointer-events',
		]);
		return {
			featureData,
			showBaseStatus,
			showLiveStatus,
			showOnlineStatus,
			failingGnssCodes,
			tooltipClasses,
		};
	}
};
</script>

<style scoped>
@import '@/assets/map-tooltip.css';

.subtitle {
	font-size: 14px;
	margin-top: 12px;
	font-weight: 600;
	line-height: 1.2;
}

.non-reliable-coords-text {
	font-size: 12px;
}
</style>